export const JOB_GROUP_ORDER = [
	"목사",
	"남전도사",
	"여전도사",
	"남직원",
	"여직원",
	"기타",
	"경비실",
];

export const getAttendanceJobGroup = (employee) => {
	const teams = employee?.employee_team_names || [];
	if (
		employee?.is_security ||
		teams.includes("경비실")
	) {
		return "경비실";
	}
	if (employee?.sort_at_bottom) {
		return "기타";
	}
	const job = employee?.job;
	const gender = employee?.gender;
	if (job === "목사") {
		return "목사";
	}
	if (job === "전도사") {
		return gender === "남" ? "남전도사" : "여전도사";
	}
	if (job === "교직원") {
		return gender === "남" ? "남직원" : "여직원";
	}
	return "기타";
};

/**
 * Returns flat list of employees sorted by job group then name,
 * with separator markers between non-empty groups.
 * Separator items: { __separator: true, group: string, key: string }
 */
export const buildSortedAttendanceRows = (employees = []) => {
	const buckets = Object.fromEntries(
		JOB_GROUP_ORDER.map((group) => [group, []]),
	);

	employees.forEach((emp) => {
		const group = getAttendanceJobGroup(emp);
		buckets[group].push(emp);
	});

	JOB_GROUP_ORDER.forEach((group) => {
		buckets[group].sort((a, b) =>
			String(a.name || "").localeCompare(String(b.name || ""), "ko"),
		);
	});

	const rows = [];
	let previousHadPeople = false;
	JOB_GROUP_ORDER.forEach((group) => {
		const list = buckets[group];
		if (!list.length) {
			return;
		}
		if (previousHadPeople) {
			rows.push({
				__separator: true,
				group,
				key: `sep-${group}`,
			});
		}
		list.forEach((emp) => rows.push(emp));
		previousHadPeople = true;
	});
	return rows;
};

/**
 * For Ant Design Table: consecutive rows with the same job group get rowSpan
 * on the first row; later rows in the group get rowSpan 0.
 */
export const buildJobGroupRowSpans = (rows = [], getEmployee = (row) => row) => {
	const spans = new Array(rows.length).fill(0);
	let index = 0;
	while (index < rows.length) {
		const group = getAttendanceJobGroup(getEmployee(rows[index]));
		let end = index + 1;
		while (
			end < rows.length &&
			getAttendanceJobGroup(getEmployee(rows[end])) === group
		) {
			end += 1;
		}
		spans[index] = end - index;
		index = end;
	}
	return spans;
};

/**
 * Groups consecutive employees that share a job category.
 * Separators stay between groups. Each group block is one label cell.
 */
export const groupAttendanceRows = (rows = []) => {
	const blocks = [];
	let index = 0;
	while (index < rows.length) {
		const row = rows[index];
		if (row.__separator) {
			blocks.push({ type: "separator", key: row.key });
			index += 1;
			continue;
		}
		const group = getAttendanceJobGroup(row);
		const employees = [];
		while (
			index < rows.length &&
			!rows[index].__separator &&
			getAttendanceJobGroup(rows[index]) === group
		) {
			employees.push(rows[index]);
			index += 1;
		}
		blocks.push({
			type: "group",
			group,
			employees,
			key: `group-${group}`,
		});
	}
	return blocks;
};

export const ATTENDANCE_STATUS_LABEL = {
	present: { text: "출근", color: "green" },
	late: { text: "지각", color: "gold" },
	absent: { text: "결석", color: "red" },
	holiday: { text: "휴일", color: "default" },
	on_leave: { text: "휴직", color: "default" },
	annual_leave: { text: "연차", color: "blue" },
	half_day: { text: "반차", color: "green" },
	menstrual_leave: { text: "생리휴가", color: "pink" },
	sick_leave: { text: "병가", color: "purple" },
	business_trip: { text: "출장", color: "cyan" },
	pending: { text: "미집계", color: "default" },
	empty: { text: "-", color: "default" },
};

const PERSONAL_HOLIDAY_KINDS = new Set(["경조사", "특별휴가", "기타", "월차"]);

/** 상단 특이사항: 고정 2열 3행 */
export const ATTENDANCE_EXCEPTION_ROWS = [
	[
		{ key: "annual_leave", label: "연차" },
		{ key: "substitute_holiday", label: "대체휴무" },
	],
	[
		{ key: "business_trip", label: "출장" },
		{ key: "local_trip", label: "시내출장" },
	],
	[
		{ key: "sick_leave", label: "병가" },
		{ key: "menstrual_leave", label: "생리휴가" },
	],
];

export const ATTENDANCE_EXCEPTION_EXTRA = [];

export const ATTENDANCE_EXCEPTION_GROUPS = [
	...ATTENDANCE_EXCEPTION_ROWS.flat(),
	...ATTENDANCE_EXCEPTION_EXTRA,
];

export const resolveAttendanceStatusLabel = (cell) => {
	if (!cell) {
		return ATTENDANCE_STATUS_LABEL.empty;
	}
	if (
		cell.has_excuse &&
		cell.excuse_category !== "대체휴무" &&
		!cell.worked_on_weekly_off
	) {
		return { text: "사유서", color: "blue" };
	}
	if (cell.worked_on_weekly_off || cell.worked_on_holiday) {
		return { text: "출근", color: "green" };
	}
	if (cell.status === "half_day") {
		if (cell.half_day === "am") {
			return { text: "오전 반차", color: "green" };
		}
		if (cell.half_day === "pm") {
			return { text: "오후 반차", color: "green" };
		}
		return { text: "반차", color: "green" };
	}
	if (cell.status === "holiday" && cell.holiday_kind === "정기휴일") {
		return { text: "정기휴무", color: "default" };
	}
	if (cell.status === "holiday" && cell.holiday_kind === "대체휴무") {
		return { text: "휴무", color: "default" };
	}
	if (cell.status === "holiday" && PERSONAL_HOLIDAY_KINDS.has(cell.holiday_kind)) {
		return { text: cell.holiday_kind, color: "blue" };
	}
	// 법정공휴일: holiday_kind에 공휴일명(개천절 등)이 들어온다.
	if (
		cell.status === "holiday" &&
		cell.holiday_kind &&
		cell.holiday_kind !== "휴일" &&
		cell.holiday_kind !== "자율출근"
	) {
		return { text: cell.holiday_kind, color: "red" };
	}
	return ATTENDANCE_STATUS_LABEL[cell.status] || ATTENDANCE_STATUS_LABEL.empty;
};

const hasLocalTripApplication = (cell) =>
	(cell.leave_applications || []).some((app) => app.leave_type === "시내출장");

const isLocalTripCell = (cell) => {
	const types = (cell.leave_applications || []).map((app) => app.leave_type);
	const hasLocal = types.includes("시내출장");
	const hasTrip = types.includes("출장");
	if (hasLocal && !hasTrip) {
		return true;
	}
	if (types.length > 0) {
		return false;
	}
	return cell.tooltip === "시내출장" || cell.holiday_kind === "시내출장";
};

/** 특이사항 배너에 보여줄 분류. 지각·결석은 제외한다. */
export const getAttendanceExceptionGroup = (cell) => {
	if (!cell) {
		return null;
	}
	if (cell.status === "sick_leave") {
		return "sick_leave";
	}
	if (cell.status === "business_trip") {
		return isLocalTripCell(cell) ? "local_trip" : "business_trip";
	}
	if (
		cell.status === "annual_leave" ||
		cell.status === "half_day" ||
		cell.holiday_kind === "월차" ||
		cell.holiday_kind === "오전반차" ||
		cell.holiday_kind === "오후반차"
	) {
		return "annual_leave";
	}
	if (cell.status === "menstrual_leave") {
		return "menstrual_leave";
	}
	if (
		(cell.has_excuse && cell.excuse_category === "대체휴무") ||
		cell.holiday_kind === "대체휴무"
	) {
		return "substitute_holiday";
	}
	if (hasLocalTripApplication(cell)) {
		return "local_trip";
	}
	return null;
};

export const halfDayExceptionLabel = (cell) => {
	if (!cell) {
		return "연차";
	}
	if (cell.status === "half_day" && cell.half_day === "am") {
		return "오전 반차";
	}
	if (cell.status === "half_day" && cell.half_day === "pm") {
		return "오후 반차";
	}
	if (cell.holiday_kind === "오전반차") {
		return "오전 반차";
	}
	if (cell.holiday_kind === "오후반차") {
		return "오후 반차";
	}
	return "연차";
};

export const isAttendanceException = (cell) =>
	getAttendanceExceptionGroup(cell) !== null;

export const formatClockTime = (clockTime) => {
	if (!clockTime) {
		return "";
	}
	return String(clockTime).slice(0, 5);
};

export const ATTENDANCE_LEGEND_ITEMS = [
	{ status: "present", label: "출근" },
	{
		status: "present",
		label: "공휴일 출근",
		cell: { status: "present", worked_on_holiday: true },
	},
	{ status: "late", label: "지각" },
	{ status: "absent", label: "결석" },
	{ status: "holiday", label: "휴일" },
	{
		status: "holiday",
		label: "정기휴무",
		cell: { status: "holiday", holiday_kind: "정기휴일" },
	},
	{ status: "annual_leave", label: "연차" },
	{
		status: "half_day",
		label: "오전 반차",
		cell: { status: "half_day", half_day: "am" },
	},
	{
		status: "half_day",
		label: "오후 반차",
		cell: { status: "half_day", half_day: "pm" },
	},
	{ status: "menstrual_leave", label: "생리휴가" },
	{ status: "sick_leave", label: "병가" },
	{ status: "business_trip", label: "출장" },
	{ status: "on_leave", label: "휴직" },
	{
		status: "present",
		label: "사유서",
		cell: { status: "present", has_excuse: true },
	},
];
